/**
 * Custom Hook: useWallet
 * Provides wallet-related API calls and state management
 *
 * Usage:
 * const { balance, loading, error, fetchBalance, payForTickets } = useWallet();
 */

import { useState, useCallback } from 'react';
import {
  WalletBalanceAPI,
  WalletTransactionAPI,
  WalletDepositAPI,
  TicketPaymentAPI,
  FinanceDashboardAPI
} from '../services/walletService';

export const useWallet = () => {
  console.log("[useWallet] ⚙️  Hook instantiated/re-rendered");

  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [unpaidTickets, setUnpaidTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  console.log("[useWallet] Initial state - balance:", balance, "| unpaidTickets:", unpaidTickets?.length || 0, "| loading:", loading);

  /**
   * Fetch wallet balance
   */
  const fetchBalance = useCallback(async (organizationId = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = organizationId
        ? await WalletBalanceAPI.getBalanceByOrg(organizationId)
        : await WalletBalanceAPI.getBalance();
      setBalance(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch wallet balance';
      setError(errorMsg);
      console.error('useWallet - fetchBalance error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch wallet transactions (CREDIT and DEBIT)
   */
  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      console.log('[useWallet] Fetching wallet transactions (CREDIT/DEBIT)...');
      const data = await WalletTransactionAPI.getWalletTransactions(params);
      console.log('[useWallet] Wallet transactions loaded:', data.length, 'transactions');
      setTransactions(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch transactions';
      setError(errorMsg);
      console.error('useWallet - fetchTransactions error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch transactions by date range
   */
  const fetchTransactionsByDateRange = useCallback(async (startDate, endDate) => {
    setLoading(true);
    setError(null);
    try {
      const data = await WalletTransactionAPI.getTransactionsByDateRange(startDate, endDate);
      setTransactions(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch transaction history';
      setError(errorMsg);
      console.error('useWallet - fetchTransactionsByDateRange error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch unpaid tickets for ticket payment
   */
  const fetchUnpaidTickets = useCallback(async (customerId = '') => {
    console.log('[useWallet] fetchUnpaidTickets called with customerId:', customerId);
    setLoading(true);
    setError(null);
    try {
      console.log('[useWallet] Calling TicketPaymentAPI.getUnpaidTickets...');
      const data = await TicketPaymentAPI.getUnpaidTickets(customerId);
      console.log('[useWallet] API response received, data:', data);
      console.log('[useWallet] Setting unpaidTickets with', Array.isArray(data) ? data.length : 0, 'items');
      setUnpaidTickets(data);
      return data;
    } catch (err) {
      if (err?.response?.status === 403) {
        // Partner users don't have access to customer unpaid tickets
        setUnpaidTickets([]);
        return [];
      }
      const errorMsg = err.message || 'Failed to fetch unpaid tickets';
      setError(errorMsg);
      console.error('[useWallet] fetchUnpaidTickets error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Pay for selected tickets
   */
  const payForTickets = useCallback(async (paymentData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TicketPaymentAPI.payForTickets(paymentData);
      // Refresh balance after successful payment
      await fetchBalance();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to process ticket payment';
      setError(errorMsg);
      console.error('useWallet - payForTickets error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchBalance]);

  /**
   * Create a wallet deposit
   */
  const createDeposit = useCallback(async (depositData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await WalletDepositAPI.createDeposit(depositData);
      // Refresh balance after successful deposit (for gateway payments)
      // Note: Bank transfers may not immediately reflect balance
      await fetchBalance();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to create deposit';
      setError(errorMsg);
      console.error('useWallet - createDeposit error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchBalance]);

  /**
   * Get pending deposits
   */
  const getPendingDeposits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const count = await WalletDepositAPI.getPendingDeposits();
      return count;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch pending deposits';
      setError(errorMsg);
      console.error('useWallet - getPendingDeposits error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get finance dashboard summary
   */
  const getDashboardSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await FinanceDashboardAPI.getDashboardSummary();
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch dashboard summary';
      setError(errorMsg);
      console.error('useWallet - getDashboardSummary error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const returnObj = {
    // State
    balance,
    transactions,
    unpaidTickets,
    loading,
    error,

    // Methods
    fetchBalance,
    fetchTransactions,
    fetchTransactionsByDateRange,
    fetchUnpaidTickets,
    payForTickets,
    createDeposit,
    getPendingDeposits,
    getDashboardSummary,
    clearError
  };

  console.log("[useWallet] Returning object with - balance:", returnObj.balance, "| unpaidTickets count:", returnObj.unpaidTickets?.length || 0, "| error:", returnObj.error);

  return returnObj;
};

export default useWallet;
